import { useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Input, Typography, message } from 'antd';
import type { InputRef } from 'antd';
import type { BatchTagsApi } from './types';

interface Props {
  personId: string;
  assignedTo: string | null;
  tagsApi: BatchTagsApi;
  /** Called after the server confirms the new value. The caller updates its row
   *  state so the optimistic edit becomes canonical. */
  onAssignedChange: (next: string | null) => void;
  /**
   * Read-only — renders the value as plain text (no editable input). Used by the
   * portal's stakeholder single-pane. Default keeps the editable input, so the
   * console is unchanged.
   */
  readOnly?: boolean;
  /**
   * Names to offer while typing — the connected CRM's owner roster (spec
   * 2026-10-02 §5.1), so a typed assignment matches the roster's spelling and
   * the backend's owner chain can resolve it. Free text still saves. Absent or
   * empty → a plain input, byte-identical to before (no CRM, roster failed).
   */
  suggestions?: string[];
}

/**
 * Inline editable "Assigned to" cell (Batch-2). Renders a bordered AntD Input
 * with an inviting placeholder — it used to look like plain text until focused,
 * and nobody could tell it was editable (gh-31 §4.2). Save fires on blur or Enter
 * with optimistic update + rollback toast on failure. Empty-after-trim is
 * normalised to null so a cleared assignment shows the input placeholder rather
 * than a stale value. Ported from the portal's
 * `src/views/BatchScreen/AssignmentCell.tsx`; the save endpoint arrives via the
 * shell-provided `tagsApi`.
 *
 * 2026-10-02: with `suggestions`, the Input is wrapped in an `AutoComplete`
 * that filters them case-insensitively by substring. Selecting an option
 * writes the option's exact text and saves it immediately.
 */
export function AssignmentCell({
  personId,
  assignedTo,
  tagsApi,
  onAssignedChange,
  readOnly,
  suggestions,
}: Props) {
  const [draft, setDraft] = useState<string>(assignedTo ?? '');
  const [busy, setBusy] = useState(false);
  const lastSavedRef = useRef<string | null>(assignedTo);
  const savingRef = useRef(false);
  const inputRef = useRef<InputRef | null>(null);

  // Keep the draft in sync when the row's value changes externally
  // (e.g., the list re-fetches and provides a new assignedTo).
  useEffect(() => {
    setDraft(assignedTo ?? '');
    lastSavedRef.current = assignedTo;
  }, [assignedTo]);

  const options = useMemo(() => {
    const needle = draft.trim().toLowerCase();
    return (suggestions ?? [])
      .filter((s) => needle === '' || s.toLowerCase().includes(needle))
      .map((s) => ({ value: s }));
  }, [suggestions, draft]);

  async function saveValue(next: string) {
    const normalized = next.trim() === '' ? null : next.trim();
    if (normalized === lastSavedRef.current) return; // no-op
    // Guards against a second save landing while the first is still in flight
    // (e.g. onSelect saves, then the input's blur — synthetic in the browser
    // when it becomes disabled, simulated directly in jsdom tests — fires too).
    // Deliberately a ref, not an optimistic write to lastSavedRef: that would
    // defeat the rollback-on-rejection path below.
    if (savingRef.current) return;
    savingRef.current = true;
    setBusy(true);
    try {
      const { assignedTo: confirmed } = await tagsApi.setAssignment(personId, normalized);
      lastSavedRef.current = confirmed;
      onAssignedChange(confirmed);
      setDraft(confirmed ?? '');
    } catch (err) {
      // Roll back the local draft to the last server-confirmed value.
      setDraft(lastSavedRef.current ?? '');
      void message.error('Couldn’t save the assignment. Try again.');
      // eslint-disable-next-line no-console
      console.error('setAssignment failed', err);
    } finally {
      savingRef.current = false;
      setBusy(false);
    }
  }

  if (readOnly) {
    return assignedTo ? (
      <Typography.Text>{assignedTo}</Typography.Text>
    ) : (
      <Typography.Text type="secondary">—</Typography.Text>
    );
  }

  const hasSuggestions = Boolean(suggestions && suggestions.length > 0);

  // When wrapped below, rc-select overrides this Input's `value` and composes
  // its own `onChange`/`onBlur` with these — the duplication here is
  // intentional for both branches, not dead props to "clean up".
  const input = (
    <Input
      ref={inputRef}
      size="small"
      placeholder="Assign to…"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      disabled={busy}
      onBlur={() => void saveValue(draft)}
      // Deferred: rc-select's own Enter handling (committing the
      // keyboard-highlighted option via onSelect) runs in the same keydown
      // dispatch as this handler. Blurring synchronously here would close/save
      // before that handler runs, saving the typed prefix instead of the pick.
      // queueMicrotask lets the current dispatch (and any state update it
      // schedules) finish first; the guard above still saves plain free text.
      onPressEnter={() => queueMicrotask(() => inputRef.current?.blur())}
      maxLength={200}
      aria-label="Assigned to"
    />
  );

  if (!hasSuggestions) return input;

  return (
    <AutoComplete
      value={draft}
      options={options}
      onChange={(v: string) => setDraft(v)}
      onSelect={(v: string) => {
        setDraft(v);
        void saveValue(v);
      }}
      disabled={busy}
      style={{ width: '100%' }}
      // jsdom/rc-virtual-list workaround: the 0x0 a11y mirror duplicates each
      // option under virtualization. Production renders the whole (small)
      // filtered roster instead of a window — harmless at roster sizes.
      virtual={false}
    >
      {input}
    </AutoComplete>
  );
}
